import React, { useCallback } from "react";
import { styled, css } from "styled-components";
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, } from "../../icons";
import Modal from "../Modal";
import { mediaQueries as media } from "../../styles";
import { useEventListener } from "../../hooks";
const LEFT_ARROW_KEY = 37;
const RIGHT_ARROW_KEY = 39;
const Container = styled.div `
  display: flex;
  flex-direction: row;

  padding: 1rem 2rem 2rem 2rem;
  ${media.down("small") `
    padding: 0;
    flex-direction: column;
  `}
`;
const PictureContainer = styled.div `
  flex: 0 0 80%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  position: relative;

  ${media.down("large") `
    flex: 0 0 70%;
  `}
  ${media.down("small") `
    flex: 0 0 100%;
  `}
`;
const btnReset = css `
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: normal;
  appearance: none;
  outline: none;
  cursor: pointer;
`;
const GoLeft = styled.button `
  ${btnReset};
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 10%;

  svg {
    position: absolute;
    left: 0;
    width: 1.875rem;
    height: 1.875rem;

    ${media.down("large") `
      width: 1.25rem;
      height: 1.25rem;
    `}
  }
  &:hover {
    svg {
      color: #292929;
      fill: #292929;
    }
  }
`;
const PictureHolder = styled.div `
  flex: 0.8;
  img {
    border-radius: 0.625rem;
  }

  ${media.down("large") `
    flex: 0.9;
  `}

  ${media.down("small") `
    flex: 1;
    img {
      border-radius: 0;
    }
  `}
`;
const GoRight = styled.button `
  ${btnReset};
  position: absolute;
  right: 0;
  bottom: 0;
  top: 0;
  width: 90%;
  svg {
    position: absolute;
    right: 0;
    width: 1.875rem;
    height: 1.875rem;

    ${media.down("large") `
      width: 1.25rem;
      height: 1.25rem;
    `}
  }
  &:hover {
    svg {
      color: #292929;
      fill: #292929;
    }
  }
`;
const Controls = styled.div `
  padding-top: 2.5rem;
  padding-left: 1.25rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const ImageToggler = styled.div `
  display: flex;
  overflow-x: scroll;
  scroll-behavior: smooth;
  width: 100%;
  margin-bottom: 3rem;
  padding-left: 4rem;
  padding-right: 4rem;
  ::-webkit-scrollbar {
    width: 0;
  }

  ${media.down("small") `
    display: none;
  `}
`;
const ToggleImageLink = styled.button `
  ${btnReset};
  margin: 0 0.9375rem 0 0;

  display: block;
  cursor: pointer;
  width: 4rem;
  height: 4rem;
  img {
    border-radius: 5px;
    width: 4rem;
    min-height: 100%;
    object-fit: cover;
    ${(props) => props.active &&
    css `
        border: 0.125rem solid #000000;
      `}

    ${media.down("small") `
        border-radius: 0;
      `}
  }
`;
const ActivePictureInfo = styled.div `
  flex: 1;
`;
const NumImage = styled.strong `
  font-weight: 600;
  margin-bottom: 2.5rem;
`;
const Caption = styled.p ``;
const PersistentInfo = styled.div `
  margin-bottom: 2rem;
`;
const SwiperArea = styled.div `
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  ${media.up("small") `
    display: none;
  `}
`;
const Image = styled.img `
  display: block;
  width: 100%;
  height: auto;
`;
const DefaultPopupGalleryImageRenderer = ({ image }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { caption, alt, src, ...imageProps } = image;
    return React.createElement(Image, { src: src, alt: alt, ...imageProps });
};
const OpenPopupGallery = ({ images, persistentInfo, initialIndex = 0, imageRenderer = DefaultPopupGalleryImageRenderer, }) => {
    let lastTouch = 0;
    let touchDelta = 0;
    const [activeIndex, setActiveIndex] = React.useState(initialIndex);
    const imageTogglerRef = React.useRef(null);
    const htmlElRef = React.useRef(null);
    const executeScrollToToggle = () => {
        if (htmlElRef.current && imageTogglerRef.current) {
            const x = htmlElRef.current.offsetLeft -
                imageTogglerRef.current.offsetLeft -
                64 -
                15;
            imageTogglerRef.current.scrollTo(x, 0);
        }
    };
    const activeImage = images[activeIndex];
    const handleToggleImageIndex = (index) => {
        setActiveIndex(index);
    };
    const handleNext = useCallback((unaryOperator) => {
        const wishActiveIndex = activeIndex + unaryOperator;
        let nextActiveIndex = wishActiveIndex < 0 ? images.length - 1 : wishActiveIndex;
        nextActiveIndex =
            nextActiveIndex > images.length - 1 ? 0 : nextActiveIndex;
        setActiveIndex(nextActiveIndex);
    }, [activeIndex, images.length]);
    const handleTouchStart = (e) => {
        lastTouch = e.nativeEvent.touches[0].clientX;
    };
    const handleTouchMove = (e) => {
        touchDelta = lastTouch - e.nativeEvent.touches[0].clientX;
        lastTouch = e.nativeEvent.touches[0].clientX;
    };
    const handleTouchEnd = () => {
        if (touchDelta > 0) {
            handleNext(-1);
        }
        else if (touchDelta < 0) {
            handleNext(1);
        }
        else {
            handleNext(1);
        }
        lastTouch = 0;
    };
    const handleKeyDown = React.useCallback((event) => {
        switch (event.keyCode) {
            case LEFT_ARROW_KEY:
                handleNext(-1);
                break;
            case RIGHT_ARROW_KEY:
                handleNext(1);
                break;
            default:
        }
    }, [handleNext]);
    useEventListener("keydown", handleKeyDown, typeof document !== "undefined" ? document : null);
    React.useEffect(() => {
        executeScrollToToggle();
    }, [activeIndex]);
    return (React.createElement(Container, null,
        React.createElement(PictureContainer, null,
            React.createElement(PictureHolder, null, imageRenderer({ image: activeImage, type: "activeImage" })),
            React.createElement(GoLeft, { onClick: () => handleNext(-1) },
                React.createElement(ChevronLeftIcon, null)),
            React.createElement(GoRight, { onClick: () => handleNext(1) },
                React.createElement(ChevronRightIcon, null)),
            React.createElement(SwiperArea, { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd })),
        React.createElement(Controls, null,
            React.createElement(ImageToggler, { ref: imageTogglerRef }, images.map((image, index) => (React.createElement(ToggleImageLink, { ref: activeIndex === index ? htmlElRef : null, onClick: () => handleToggleImageIndex(index), active: activeIndex === index, key: image.key }, imageRenderer({ image, type: "toggle" }))))),
            React.createElement(ActivePictureInfo, null,
                React.createElement(NumImage, null,
                    activeIndex + 1,
                    " / ",
                    images.length),
                React.createElement(Caption, null, activeImage.caption)),
            persistentInfo && React.createElement(PersistentInfo, null, persistentInfo()),
            activeImage.credits && React.createElement("span", null, activeImage.credits))));
};
export const PopupGallery = ({ images, isOpen, onRequestClose, persistentInfo, xl = true, initialIndex = 0, label = "", imageRenderer = DefaultPopupGalleryImageRenderer, }) => (React.createElement(React.Fragment, null, isOpen && (React.createElement(Modal, { label: label, onRequestClose: onRequestClose, xl: xl }, isOpen && (React.createElement(OpenPopupGallery, { imageRenderer: imageRenderer, images: images, persistentInfo: persistentInfo, initialIndex: initialIndex }))))));
export default PopupGallery;
//# sourceMappingURL=PopupGallery.js.map